/**
 * PhoneFrame.jsx
 * Reusable phone simulator frame component with FAB actions
 */
import React from 'react';
import { 
  Home, Calendar, Utensils, Menu, Plus, Camera, 
  Search, Sparkles, Heart
} from 'lucide-react';
import { toast } from 'sonner';
import WinterHat from './WinterHat';

export const PhoneFrame = ({ 
  children, 
  showFab, 
  onFabClick, 
  fabMenuOpen, 
  setFabMenuOpen, 
  hideNav,
  currentScreen,
  selectedTab,
  setSelectedTab,
  setCurrentScreen,
  onNutritionAction,  // New callback for nutrition actions
  winterHatEnabled = false  // Winter hat on logo
}) => {
  // Handle nutrition FAB actions - 4 options like Kundenportal
  const handleNutritionAction = (action) => {
    setFabMenuOpen(false);
    // Call the callback if provided
    if (onNutritionAction) {
      onNutritionAction(action);
    } else {
      // Fallback toast messages
      switch (action) {
        case 'ai_help':
          toast.info('🤖 KI-Kalkulator - AI Assistant würde gestartet');
          break;
        case 'photo':
          toast.info('📷 Mahlzeit fotografieren - Kamera würde geöffnet');
          break;
        case 'search':
          toast.info('🔍 Lebensmittel suchen - Suchfunktion würde geöffnet');
          break;
        case 'create':
          toast.info('🍳 Rezept erstellen - Editor würde geöffnet');
          break;
        default:
          break;
      }
    }
  };

  return (
    <div className="relative mx-auto" style={{ width: '375px' }}>
      {/* Phone outer frame */}
      <div className="bg-zinc-900 rounded-[3rem] p-3 shadow-2xl border border-zinc-700 relative">
        {/* Notch */}
        <div className="absolute top-6 left-1/2 transform -translate-x-1/2 w-32 h-7 bg-black rounded-full z-20" />
        
        {/* Screen */}
        <div className="bg-zinc-950 rounded-[2.5rem] relative" style={{ height: '700px', overflow: 'hidden' }}>
          {/* Status Bar */}
          <div className="h-12 bg-transparent flex items-center justify-between px-8 pt-2 relative z-20">
            <span className="text-white text-sm font-medium">9:41</span>
            <div className="flex items-center gap-1">
              <div className="w-4 h-2 border border-white rounded-sm">
                <div className="w-3 h-1.5 bg-yellow-500 rounded-sm" />
              </div>
            </div>
          </div>
          
          {/* Content Area - children includes effects + screen content */}
          <div className={`h-full overflow-y-auto relative ${hideNav ? '' : 'pb-24'}`}>
            {children}
          </div>
          
          {/* Bottom Navigation - nur wenn eingeloggt */}
          {!hideNav && (
          <div className="absolute bottom-0 left-0 right-0 h-20 bg-zinc-900/95 backdrop-blur border-t border-zinc-800 flex items-center justify-around px-2 z-30">
            {[
              { id: 'home', icon: Home, label: 'Home' },
              { id: 'bookings', icon: Calendar, label: 'Termine' },
              { id: 'nutrition', icon: Utensils, label: 'Ernährung' },
              { id: 'fitness', icon: Heart, label: 'Gesundheit' },
              { id: 'profile', icon: Menu, label: 'Mehr' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => { setSelectedTab(tab.id); setCurrentScreen(tab.id); setFabMenuOpen && setFabMenuOpen(false); }}
                className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-colors ${
                  selectedTab === tab.id ? 'text-yellow-500' : 'text-zinc-500'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                <span className="text-[9px]">{tab.label}</span>
              </button>
            ))}
          </div>
          )}
        </div>
        
        {/* FAB and Menu - Outside overflow container but inside phone frame */}
        {showFab && (
          <>
            {/* FAB Menu Overlay - only for nutrition screen */}
            {fabMenuOpen && currentScreen === 'nutrition' && (
              <div 
                className="absolute inset-0 z-40 rounded-[2.5rem]"
                style={{ top: '12px', left: '12px', right: '12px', bottom: '12px' }}
                onClick={() => setFabMenuOpen(false)}
              >
                {/* Backdrop */}
                <div className="absolute inset-0 bg-black/60 rounded-[2.5rem]" />
                {/* Menu - 4 options like Kundenportal */}
                <div 
                  className="absolute w-52 bg-zinc-800 rounded-xl overflow-hidden shadow-2xl border border-zinc-700"
                  style={{ bottom: '100px', right: '16px' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Ernährung Menu - 4 Optionen wie Kundenportal */}
                  <>
                    {/* 1. KI-Kalkulator - Highlight */}
                    <button 
                      onClick={() => handleNutritionAction('ai_help')}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-zinc-700/50 transition-colors border-b border-zinc-700/50 bg-gradient-to-r from-yellow-500/20 to-orange-500/20"
                    >
                      <div className="w-8 h-8 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-yellow-400 text-xs font-medium">🤖 KI-Kalkulator</span>
                    </button>
                    
                    {/* 2. Mahlzeit fotografieren */}
                    <button 
                      onClick={() => handleNutritionAction('photo')}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-zinc-700/50 transition-colors border-b border-zinc-700/50"
                    >
                      <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                        <Camera className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-white text-xs">📷 Mahlzeit fotografieren</span>
                    </button>
                    
                    {/* 3. Lebensmittel suchen */}
                    <button 
                      onClick={() => handleNutritionAction('search')}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-zinc-700/50 transition-colors border-b border-zinc-700/50"
                    >
                      <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                        <Search className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-white text-xs">🔍 Lebensmittel suchen</span>
                    </button>
                    
                    {/* 4. Rezept erstellen */}
                    <button 
                      onClick={() => handleNutritionAction('create')}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-zinc-700/50 transition-colors"
                    >
                      <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                        <Utensils className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-white text-xs">🍳 Rezept erstellen</span>
                    </button>
                  </>
                </div>
              </div>
            )}
            
            {/* FAB Button */}
            <button
              onClick={() => {
                // On Termine screen: go directly to booking
                if (currentScreen === 'bookings') {
                  setCurrentScreen('booking-new');
                } else {
                  // On Nutrition screen: open menu
                  setFabMenuOpen(!fabMenuOpen);
                }
              }}
              className={`absolute w-11 h-11 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 z-50 ${
                fabMenuOpen ? 'bg-yellow-500 rotate-45' : 'bg-yellow-500 hover:bg-yellow-400'
              }`}
              style={{ bottom: '100px', right: '20px' }}
            >
              <Plus className="w-5 h-5 text-black" strokeWidth={2.5} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PhoneFrame;
